import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, ArrowRight, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { AffiliateDisclosure, AMAZON_LINK } from '@/components/AffiliateFooter';

function BlogList() {
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/blog/articles').then(r => setArticles(r.data.articles)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="blog-page">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white mb-8 text-sm">&larr; Back</button>
        
        <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          The Brand OS <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">Blog</span>
        </h1>
        <p className="text-gray-400 mb-12">Science-backed guides to help you look and feel your best.</p>

        <div className="grid gap-8">
          {articles.map(article => (
            <article
              key={article.id}
              onClick={() => navigate(`/blog/${article.slug}`)}
              className="group cursor-pointer rounded-2xl border border-white/10 bg-white/5 overflow-hidden hover:border-amber-500/30 transition-all"
              data-testid={`blog-card-${article.slug}`}
            >
              <div className="md:flex">
                <div className="md:w-1/3">
                  <img src={article.image} alt={article.title} className="w-full h-48 md:h-full object-cover" />
                </div>
                <div className="md:w-2/3 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 capitalize">{article.category}</span>
                    <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{article.published_at}</span>
                  </div>
                  <h2 className="text-xl font-bold mb-2 group-hover:text-amber-400 transition-colors">{article.title}</h2>
                  <p className="text-gray-400 text-sm mb-4">{article.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 flex items-center gap-1"><User className="w-3 h-3" />{article.author}</span>
                    <span className="text-amber-400 text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Read More <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10">
          <AffiliateDisclosure className="text-gray-500" />
        </div>
      </div>
    </div>
  );
}

function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/blog/articles/${slug}`).then(r => setArticle(r.data)).catch(() => navigate('/blog'));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!article) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="blog-article-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <button onClick={() => navigate('/blog')} className="text-gray-400 hover:text-white mb-8 text-sm flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> All Articles
        </button>

        <img src={article.image} alt={article.title} className="w-full h-64 object-cover rounded-2xl mb-8" />

        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 capitalize">{article.category}</span>
          <span className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" />{article.published_at}</span>
          <span className="text-xs text-gray-500 flex items-center gap-1"><User className="w-3 h-3" />{article.author}</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>{article.title}</h1>

        <div className="prose prose-invert prose-sm max-w-none">
          {article.content.split('\n\n').map((block, i) => {
            if (block.startsWith('## ')) {
              return <h2 key={i} className="text-xl font-bold text-white mt-8 mb-3">{block.replace('## ', '')}</h2>;
            }
            if (block.startsWith('### ')) {
              return <h3 key={i} className="text-lg font-semibold text-white mt-6 mb-2">{block.replace('### ', '')}</h3>;
            }
            if (block.includes('\n- ')) {
              const [intro, ...items] = block.split('\n- ');
              return (
                <div key={i}>
                  {intro && <p className="text-gray-300 mb-2">{intro}</p>}
                  <ul className="list-disc pl-6 space-y-1">
                    {items.map((item, j) => <li key={j} className="text-gray-300">{item}</li>)}
                  </ul>
                </div>
              );
            }
            if (block.startsWith('1. ')) {
              const items = block.split('\n').filter(l => l.match(/^\d+\./));
              return (
                <ol key={i} className="list-decimal pl-6 space-y-1">
                  {items.map((item, j) => <li key={j} className="text-gray-300">{item.replace(/^\d+\.\s*/, '')}</li>)}
                </ol>
              );
            }
            return <p key={i} className="text-gray-300 leading-relaxed">{block}</p>;
          })}
        </div>

        {/* Amazon Product Recommendation Box */}
        <div className="mt-10 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-6" data-testid="blog-amazon-box">
          <p className="text-sm font-semibold text-amber-400 mb-1">Recommended Products</p>
          <p className="text-xs text-gray-400 mb-4">Find the products mentioned in this article and more on Amazon.</p>
          <a
            href={AMAZON_LINK}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF9900] text-white text-sm font-semibold rounded-xl hover:bg-[#e88b00] transition-colors"
            data-testid="blog-amazon-btn"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2} />
            Check Prices on Amazon
          </a>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-gray-400 mb-4">Ready to start your personal improvement journey?</p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-3 bg-amber-500 text-black rounded-xl font-semibold hover:bg-amber-400 transition-all"
            data-testid="blog-cta-btn"
          >
            Try Brand OS Free for 3 Days
          </button>
        </div>

        <div className="mt-8 pt-4 border-t border-white/10">
          <AffiliateDisclosure className="text-gray-500" />
        </div>
      </div>
    </div>
  );
}

export { BlogList, BlogArticle };
